import axios from 'axios';

// Create axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if refresh is in progress to prevent multiple simultaneous refresh calls
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Clear all auth data and redirect to login
export const clearAuthAndRedirect = (message = null) => {
  // Only set session expired message if user had an active session
  // Check if there was a token BEFORE clearing (had a session)
  const hadSession = !!localStorage.getItem('accessToken') || !!localStorage.getItem('refreshToken');
  
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('sessionConfig');

  // Store session expired message ONLY if there was a valid session
  // and we're not already on a public page
  const publicPaths = ['/login', '/accept-invite', '/auth/callback'];
  const isOnPublicPage = publicPaths.some(path => window.location.pathname.startsWith(path));
  
  if (message && hadSession && !isOnPublicPage) {
    sessionStorage.setItem('sessionMessage', message);
  }

  // Only redirect if not already on a public page
  if (!isOnPublicPage) {
    window.location.href = '/login';
  }
};

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle token refresh with rotation
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Check if this is the refresh endpoint itself failing
      if (originalRequest.url?.includes('/auth/refresh')) {
        clearAuthAndRedirect('Your session has expired. Please login again.');
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue requests while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');

        if (!refreshToken) {
          clearAuthAndRedirect('Your session has expired. Please login again.');
          return Promise.reject(error);
        }

        // Try to refresh token - server rotates refresh token
        const response = await axios.post(
          `${api.defaults.baseURL}/auth/refresh`,
          { refresh_token: refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        );

        const { access_token, refresh_token: new_refresh_token } = response.data;

        // Store new tokens (refresh token is rotated)
        localStorage.setItem('accessToken', access_token);
        if (new_refresh_token) {
          localStorage.setItem('refreshToken', new_refresh_token);
        }

        // Process queued requests
        processQueue(null, access_token);

        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear queue and force logout
        processQueue(refreshError, null);
        clearAuthAndRedirect('Your session has expired. Please login again.');
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Fetch session configuration from backend
export const fetchSessionConfig = async () => {
  try {
    const response = await api.get('/auth/session-config');
    localStorage.setItem('sessionConfig', JSON.stringify(response.data));
    return response.data;
  } catch (error) {
    // Default values if fetch fails
    return {
      access_token_expire_minutes: 30,
      session_idle_timeout_minutes: 30
    };
  }
};

/**
 * Fetch task type dropdowns for UI selection
 * @param {string|number} projectId - Optional project ID to filter types by project scope
 * @returns {Promise<Array>} Array of TaskDropdownOption objects with id, name, icon_url, icon_type
 */
export const fetchTaskTypeDropdowns = async (projectId = null) => {
  try {
    let url = '/task-types/dropdown';
    
    // Use project-scoped dropdown if projectId is provided
    if (projectId) {
      url = `/task-types/dropdown/project/${projectId}`;
    }
    
    const response = await api.get(url);
    return response.data.items || [];
  } catch (error) {
    console.error('Failed to fetch task type dropdowns:', error);
    // Return empty array on error - will fall back to default types
    return [];
  }
};

export default api;
